/**
 * プリセット詳細ページ（Presentation）
 * 左: プリセット情報、右: マッチング結果
 */

import { Button } from "components/ui/Button";
import { Loading } from "components/ui/Loading/Loading";
import { StatusBadge } from "components/ui/StatusBadge";
import { industryCategoryMap } from "constants/email-card";
import type { MatchingResultItem } from "hooks/use-free-matching-api";
import type { Preset } from "hooks/use-preset-api";
import { ArrowLeft, Pencil } from "lucide-react";
import type { FC } from "react";
import { Link } from "react-router-dom";
import { MatchingResultCard } from "routes/emails/EmailDetailPage/components/MatchingResultCard";

const PRESET_TYPE_LABELS: Record<string, string> = {
  hr: "人材",
  matter: "案件",
};

const REMOTE_LABELS: Record<string, string> = {
  full_remote: "フルリモート",
  hybrid: "ハイブリッド",
  onsite_only: "出社のみ",
};

const HR_TYPE_LABELS: Record<string, string> = {
  fullTimeEmployee: "正社員",
  contractEmployee: "契約社員",
  freelancer: "フリーランス",
};

interface PresetDetailPageProps {
  preset: Preset | null;
  loading: boolean;
  results: MatchingResultItem[];
  resultsTotal: number;
  resultsLoading: boolean;
  onPropose: (result: MatchingResultItem) => void;
}

/**
 * プリセット詳細ページ
 * @param {PresetDetailPageProps} props コンポーネントProps
 * @return {JSX.Element} プリセット詳細
 */
export const PresetDetailPage: FC<PresetDetailPageProps> = ({
  preset,
  loading,
  results,
  resultsTotal,
  resultsLoading,
  onPropose,
}) => {
  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <Loading size="lg" />
      </div>
    );
  }

  if (!preset) {
    return (
      <div className="text-center py-12 text-base-content/50">
        <p className="text-lg">プリセットが見つかりません</p>
        <div className="mt-4">
          <Button asChild variant="ghost" size="sm">
            <Link to="/settings/presets">一覧に戻る</Link>
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div>
      {/* ヘッダー */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <Button asChild variant="ghost" size="sm" shape="circle">
            <Link to="/settings/presets">
              <ArrowLeft className="w-4 h-4" />
            </Link>
          </Button>
          <div>
            <h1 className="text-xl font-bold">{preset.name}</h1>
            <div className="flex items-center gap-2 mt-1">
              <StatusBadge tone="tag-neutral">
                {PRESET_TYPE_LABELS[preset.presetType] || preset.presetType}
              </StatusBadge>
              <StatusBadge
                tone={preset.isActive ? "soft-success" : "soft-neutral"}
              >
                {preset.isActive ? "有効" : "無効"}
              </StatusBadge>
            </div>
          </div>
        </div>
        <Button asChild variant="outline" size="sm">
          <Link to={`/settings/presets/${preset.id}/edit`}>
            <Pencil className="w-3.5 h-3.5" />
            編集
          </Link>
        </Button>
      </div>

      {/* 左右レイアウト */}
      <div className="flex gap-6">
        {/* 左: プリセット情報 */}
        <div className="w-80 shrink-0 space-y-4">
          <PresetInfoPanel preset={preset} />
        </div>

        {/* 右: マッチング結果 */}
        <div className="flex-1 min-w-0">
          <ResultsPanel
            results={results}
            resultsTotal={resultsTotal}
            loading={resultsLoading}
            onPropose={onPropose}
          />
        </div>
      </div>
    </div>
  );
};

/* ---------- 左パネル: プリセット情報 ---------- */

/**
 * プリセット情報パネル
 * @param {{ preset: Preset }} props プリセット情報
 * @return {JSX.Element} プリセット情報パネル
 */
const PresetInfoPanel: FC<{ preset: Preset }> = ({ preset }) => {
  return (
    <>
      {/* 基本情報 */}
      <div className="bg-base-100 rounded-lg border p-4">
        <h3 className="font-semibold mb-3 text-sm">基本情報</h3>
        <dl className="space-y-2 text-sm">
          <DetailItem label="スキル">
            {preset.skills?.length ? (
              <div className="flex flex-wrap gap-1">
                {preset.skills.map(s => (
                  <span key={s} className="badge badge-outline badge-xs">
                    {s}
                  </span>
                ))}
              </div>
            ) : (
              <span className="text-base-content/40">-</span>
            )}
          </DetailItem>
          <DetailItem label="単価">
            {preset.reward ? `${preset.reward.toLocaleString()} 円` : "-"}
          </DetailItem>
          <DetailItem label="勤務地">{preset.station || "-"}</DetailItem>
          <DetailItem label="リモート">
            {preset.isRemote != null
              ? preset.isRemote
                ? `可 (${REMOTE_LABELS[preset.remoteFrequency ?? ""] ?? preset.remoteFrequency ?? ""})`
                : "不可"
              : "-"}
          </DetailItem>
          <DetailItem label="稼働開始">
            {preset.availableFrom || "-"}
          </DetailItem>
          <DetailItem label="業界">
            {industryCategoryMap[preset.industryCategory ?? ""] ??
              preset.industryCategory ??
              "-"}
          </DetailItem>
        </dl>
      </div>

      {/* HR/Matter 固有 */}
      {preset.presetType === "hr" ? (
        <div className="bg-base-100 rounded-lg border p-4">
          <h3 className="font-semibold mb-3 text-sm">人材情報</h3>
          <dl className="space-y-2 text-sm">
            <DetailItem label="年齢">
              {preset.age != null ? `${preset.age} 歳` : "-"}
            </DetailItem>
            <DetailItem label="雇用形態">
              {preset.hrTypes
                ? (HR_TYPE_LABELS[preset.hrTypes] ?? preset.hrTypes)
                : "-"}
            </DetailItem>
            <DetailItem label="日本国籍">
              {preset.isJapanese != null
                ? preset.isJapanese
                  ? "はい"
                  : "いいえ"
                : "-"}
            </DetailItem>
          </dl>
        </div>
      ) : (
        <div className="bg-base-100 rounded-lg border p-4">
          <h3 className="font-semibold mb-3 text-sm">案件情報</h3>
          <dl className="space-y-2 text-sm">
            <DetailItem label="フリーランス可">
              {preset.freelanceEnabled != null
                ? preset.freelanceEnabled
                  ? "はい"
                  : "いいえ"
                : "-"}
            </DetailItem>
            <DetailItem label="年齢制限">
              {preset.ageLimitation != null
                ? `${preset.ageLimitation} 歳`
                : "-"}
            </DetailItem>
            <DetailItem label="外国籍可">
              {preset.otherCountriesEnabled != null
                ? preset.otherCountriesEnabled
                  ? "はい"
                  : "いいえ"
                : "-"}
            </DetailItem>
          </dl>
        </div>
      )}

      {/* メモ */}
      {preset.note && (
        <div className="bg-base-100 rounded-lg border p-4">
          <h3 className="font-semibold mb-2 text-sm">メモ</h3>
          <p className="text-sm whitespace-pre-wrap">{preset.note}</p>
        </div>
      )}
    </>
  );
};

/**
 * 詳細項目の表示コンポーネント
 * @param {object} props ラベルと値
 * @return {JSX.Element} dt/dd ペア
 */
const DetailItem: FC<{ label: string; children: React.ReactNode }> = ({
  label,
  children,
}) => (
  <div>
    <dt className="text-base-content/60 text-xs mb-0.5">{label}</dt>
    <dd>{children}</dd>
  </div>
);

/* ---------- 右パネル: マッチング結果 ---------- */

interface ResultsPanelProps {
  results: MatchingResultItem[];
  resultsTotal: number;
  loading: boolean;
  onPropose: (result: MatchingResultItem) => void;
}

/**
 * マッチング結果パネル
 * @param {ResultsPanelProps} props マッチ結果情報
 * @return {JSX.Element} マッチ結果一覧
 */
const ResultsPanel: FC<ResultsPanelProps> = ({
  results,
  resultsTotal,
  loading,
  onPropose,
}) => {
  return (
    <div>
      <div className="flex items-center gap-2 mb-4">
        <h2 className="font-semibold">マッチング結果</h2>
        {resultsTotal > 0 && (
          <StatusBadge tone="soft-neutral">{resultsTotal}</StatusBadge>
        )}
      </div>

      {loading ? (
        <div className="flex justify-center items-center h-32">
          <Loading size="md" />
        </div>
      ) : results.length === 0 ? (
        <div className="bg-white rounded-lg shadow p-6 text-center">
          <p className="text-gray-500">
            まだマッチ結果がありません。自動マッチングが実行されると結果が表示されます。
          </p>
        </div>
      ) : (
        <div className="space-y-3">
          {[...results]
            .sort((a, b) => b.totalScore - a.totalScore)
            .map(result => (
              <MatchingResultCard
                key={result.id}
                result={result}
                onPropose={onPropose}
                onCompare={() => {
                  if (result.matchedEmailId) {
                    window.open(
                      `/emails/search/${result.matchedEmailId}`,
                      "_blank",
                    );
                  }
                }}
              />
            ))}
        </div>
      )}
    </div>
  );
};
